import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, getAuthHeader } from "@/context/AuthContext";
import { Plus, Search, X, Briefcase, AlertTriangle, MapPin } from "lucide-react";

const LIMIT = 25;
const SITUACOES = ["Novo","Em curso","Validar","Findo","Aguarda Documentos/Informações","Aguarda Pagamento"];
const SITUACAO_COLORS = {
  "Novo":"bg-yellow-100 text-yellow-800","Em curso":"bg-blue-100 text-blue-800",
  "Validar":"bg-purple-100 text-purple-800","Findo":"bg-green-100 text-green-800",
  "Aguarda Documentos/Informações":"bg-orange-100 text-orange-800","Aguarda Pagamento":"bg-indigo-100 text-indigo-800"
};
const ENTIDADES = ["Tribunal","SEF","AT","C.R.Civil","C.R.Centrais","C.Municipal","IMT","MNE","Outra"];
const EMPTY = {
  numero_processo:"",prioridade:"Normal",situacao:"Novo",nome_cliente:"",pasta:"",tipo_processo:"",
  entidade:"",outra_entidade:"",numero_entidade:"",data_entrada:"",assunto:"",observacoes:"",equipa:[],
  morada_cliente:"",codigo_postal_cliente:"",localidade_cliente:"",contato_cliente:"",email_cliente:"",contribuinte_cliente:"",
  nome_contraparte:"",contato_contraparte:"",email_contraparte:"",mandatario_contraparte:"",contato_mandatario:"",email_mandatario:""
};

/* ── Modal ─────────────────────────────────────────────────────────────────── */
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18}/></button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">{children}</div>
      </div>
    </div>
  );
}

/* ── FormField — FORA do componente principal para evitar desmontagem
      no cada re-render (causa de perda de foco no input) ──────────────────── */
function FormField({ label, name, type="text", required=false, form, onFieldChange, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children || (
        <input
          type={type}
          value={(form && name) ? (form[name] || "") : ""}
          onChange={e => onFieldChange && onFieldChange(name, e.target.value)}
          required={required}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"
        />
      )}
    </div>
  );
}

/* ── Main Component ─────────────────────────────────────────────────────────── */
export default function ProcessosList() {
  const [processos, setProcessos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [situacaoFilter, setSituacaoFilter] = useState("");
  const [prioridadeFilter, setPrioridadeFilter] = useState("");
  const [dataInicioFilter, setDataInicioFilter] = useState("");
  const [dataFimFilter, setDataFimFilter] = useState("");
  const [escritorioFilter, setEscritorioFilter] = useState("");
  const [page, setPage] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("geral");
  const navigate = useNavigate();

  /* Stable field-change handler — passed to FormField so it doesn't recreate
     on every render and cause focus loss */
  const handleFieldChange = useCallback((name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(async () => {
    setLoading(true);
    const params = {
      search, situacao: situacaoFilter, prioridade: prioridadeFilter,
      data_inicio: dataInicioFilter, data_fim: dataFimFilter,
      escritorio: escritorioFilter, skip: page * LIMIT, limit: LIMIT
    };
    const { data } = await axios.get(`${API}/processos`, { params, headers: getAuthHeader() });
    setProcessos(data.processos); setTotal(data.total); setLoading(false);
  }, [search, situacaoFilter, prioridadeFilter, dataInicioFilter, dataFimFilter, escritorioFilter, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/utilizadores`, { headers: getAuthHeader() }).then(r => setUsers(r.data)).catch(() => {});
  }, []); // carrega utilizadores uma única vez (getAuthHeader é referência estável de módulo)

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await axios.post(`${API}/processos`, form, { headers: getAuthHeader() });
      setShowModal(false); setForm(EMPTY); load();
    } catch (err) {
      alert(err.response?.data?.detail || "Erro ao guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Briefcase size={22} className="text-[#0b679e]" /> Processos
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">{total} processo(s) encontrado(s)</p>
        </div>
        <button onClick={() => { setForm(EMPTY); setActiveTab("geral"); setShowModal(true); }}
          data-testid="novo-processo-btn"
          className="flex items-center gap-2 bg-[#0b679e] hover:bg-[#084e78] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Plus size={16}/> Novo Processo
        </button>
      </div>

      {/* Escritório tabs */}
      <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1.5 w-fit">
        {[
          { key: "",        label: "Todos os Escritórios", icon: null },
          { key: "lisboa",  label: "Lisboa",               icon: "🏛" },
          { key: "acores",  label: "Açores / PDL",         icon: "🌊" },
        ].map(({ key, label, icon }) => (
          <button key={key} data-testid={`tab-escritorio-${key || "todos"}`}
            onClick={() => { setEscritorioFilter(key); setPage(0); }}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
              escritorioFilter === key ? "bg-[#0b679e] text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
            }`}>
            {icon && <span>{icon}</span>}
            {label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={search} onChange={e=>{setSearch(e.target.value);setPage(0);}} placeholder="Pesquisar processo, cliente..."
            data-testid="search-processos"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/>
        </div>
        <select value={situacaoFilter} onChange={e=>{setSituacaoFilter(e.target.value);setPage(0);}}
          data-testid="filter-situacao"
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]">
          <option value="">Todas as situações</option>
          {SITUACOES.map(s=><option key={s}>{s}</option>)}
        </select>
        <select value={prioridadeFilter} onChange={e=>{setPrioridadeFilter(e.target.value);setPage(0);}}
          data-testid="filter-prioridade"
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]">
          <option value="">Todas prioridades</option>
          <option>Normal</option><option>Urgente</option>
        </select>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500 whitespace-nowrap">De:</label>
          <input type="date" value={dataInicioFilter} onChange={e=>{setDataInicioFilter(e.target.value);setPage(0);}}
            data-testid="filter-data-inicio"
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500 whitespace-nowrap">Até:</label>
          <input type="date" value={dataFimFilter} onChange={e=>{setDataFimFilter(e.target.value);setPage(0);}}
            data-testid="filter-data-fim"
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/>
        </div>
        {(search||situacaoFilter||prioridadeFilter||dataInicioFilter||dataFimFilter) && (
          <button onClick={()=>{setSearch("");setSituacaoFilter("");setPrioridadeFilter("");setDataInicioFilter("");setDataFimFilter("");setPage(0);}}
            className="px-3 py-2 text-slate-500 hover:text-slate-700 border border-slate-300 rounded-lg text-sm flex items-center gap-1">
            <X size={14}/> Limpar
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {(() => {
          if (loading) return (
            <div className="flex items-center justify-center h-40">
              <div className="w-7 h-7 border-4 border-[#0b679e] border-t-[#F5A623] rounded-full animate-spin"></div>
            </div>
          );
          if (processos.length === 0) return (
            <div className="text-center py-16 text-slate-400">
              <Briefcase size={40} className="mx-auto mb-3 opacity-30"/>
              <p>Nenhum processo encontrado</p>
            </div>
          );
          return (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {["Nº Processo","Cliente","Situação","Prioridade","Data Entrada","Pasta"].map(h=>(
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {processos.map(p => (
                    <tr key={p.id} onClick={() => navigate(`/processos/${p.id}`)}
                      data-testid={`processo-row-${p.id}`}
                      className="hover:bg-slate-50 cursor-pointer transition-colors">
                      <td className="px-4 py-3 font-medium text-[#0b679e]">{p.numero_processo}</td>
                      <td className="px-4 py-3 text-slate-900 max-w-xs truncate">{p.nome_cliente || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${SITUACAO_COLORS[p.situacao]||"bg-slate-100 text-slate-700"}`}>
                          {p.situacao || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {p.prioridade === "Urgente" ? (
                          <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                            <AlertTriangle size={10}/> Urgente
                          </span>
                        ) : <span className="text-xs text-slate-500">Normal</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-500">{p.data_entrada || "—"}</td>
                      <td className="px-4 py-3 text-slate-500">
                        <div className="flex items-center gap-1.5">
                          {/PDL/i.test(p.pasta || "") && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-teal-100 text-teal-700 border border-teal-200 whitespace-nowrap">
                              <MapPin size={8}/> Açores
                            </span>
                          )}
                          <span className="truncate max-w-[120px]">{p.pasta || "—"}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}

        {total > LIMIT && (
          <div className="border-t border-slate-200 px-4 py-3 flex items-center justify-between text-sm text-slate-600">
            <span>Página {page+1} de {Math.ceil(total/LIMIT)}</span>
            <div className="flex gap-2">
              <button disabled={page===0} onClick={()=>setPage(p=>p-1)}
                className="px-3 py-1 border border-slate-300 rounded-lg disabled:opacity-40 hover:bg-slate-50">Anterior</button>
              <button disabled={(page+1)*LIMIT>=total} onClick={()=>setPage(p=>p+1)}
                className="px-3 py-1 border border-slate-300 rounded-lg disabled:opacity-40 hover:bg-slate-50">Seguinte</button>
            </div>
          </div>
        )}
      </div>

      {/* New Processo Modal */}
      {showModal && (
        <Modal title="Novo Processo" onClose={() => setShowModal(false)}>
          <div className="flex gap-2 mb-5 border-b border-slate-200 pb-3">
            {[["geral","Geral"],["cliente","Cliente"],["contraparte","Contraparte"]].map(([k,l])=>(
              <button key={k} onClick={()=>setActiveTab(k)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTab===k?"bg-[#0b679e] text-white":"text-slate-600 hover:bg-slate-100"}`}>
                {l}
              </button>
            ))}
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {activeTab==="geral" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Nº Processo" name="numero_processo" required form={form} onFieldChange={handleFieldChange} />
                  <FormField label="Pasta" name="pasta" form={form} onFieldChange={handleFieldChange} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Prioridade" form={form} onFieldChange={handleFieldChange}>
                    <select value={form.prioridade} onChange={e=>setForm(p=>({...p,prioridade:e.target.value}))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]">
                      <option>Normal</option><option>Urgente</option>
                    </select>
                  </FormField>
                  <FormField label="Situação" form={form} onFieldChange={handleFieldChange}>
                    <select value={form.situacao} onChange={e=>setForm(p=>({...p,situacao:e.target.value}))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]">
                      {SITUACOES.map(s=><option key={s}>{s}</option>)}
                    </select>
                  </FormField>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Entidade" form={form} onFieldChange={handleFieldChange}>
                    <select value={form.entidade} onChange={e=>setForm(p=>({...p,entidade:e.target.value}))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]">
                      <option value="">—</option>
                      {ENTIDADES.map(e=><option key={e}>{e}</option>)}
                    </select>
                  </FormField>
                  <FormField label="Tipo de Processo" name="tipo_processo" form={form} onFieldChange={handleFieldChange} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Data de Entrada" name="data_entrada" type="date" form={form} onFieldChange={handleFieldChange} />
                  <FormField label="Nº na Entidade" name="numero_entidade" form={form} onFieldChange={handleFieldChange} />
                </div>
                <FormField label="Assunto" name="assunto" form={form} onFieldChange={handleFieldChange} />
                <FormField label="Observações" form={form} onFieldChange={handleFieldChange}>
                  <textarea value={form.observacoes} onChange={e=>setForm(p=>({...p,observacoes:e.target.value}))}
                    rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/>
                </FormField>
                <FormField label="Equipa (responsáveis)" form={form} onFieldChange={handleFieldChange}>
                  <div className="border border-slate-300 rounded-lg p-3 max-h-36 overflow-y-auto space-y-1">
                    {users.map(u => (
                      <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={form.equipa.includes(u.id)}
                          onChange={e => setForm(p => ({...p, equipa: e.target.checked ? [...p.equipa,u.id] : p.equipa.filter(x=>x!==u.id)}))}
                          className="rounded" />
                        {u.nome} {u.apelido}
                      </label>
                    ))}
                  </div>
                </FormField>
              </>
            )}
            {activeTab==="cliente" && (
              <>
                <FormField label="Nome do Cliente" name="nome_cliente" form={form} onFieldChange={handleFieldChange} />
                <FormField label="Contribuinte (NIF/NIPC)" name="contribuinte_cliente" form={form} onFieldChange={handleFieldChange} />
                <FormField label="Morada" name="morada_cliente" form={form} onFieldChange={handleFieldChange} />
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Código Postal" name="codigo_postal_cliente" form={form} onFieldChange={handleFieldChange} />
                  <FormField label="Localidade" name="localidade_cliente" form={form} onFieldChange={handleFieldChange} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Contato" name="contato_cliente" form={form} onFieldChange={handleFieldChange} />
                  <FormField label="Email" name="email_cliente" type="email" form={form} onFieldChange={handleFieldChange} />
                </div>
              </>
            )}
            {activeTab==="contraparte" && (
              <>
                <FormField label="Nome da Contraparte" name="nome_contraparte" form={form} onFieldChange={handleFieldChange} />
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Contato Contraparte" name="contato_contraparte" form={form} onFieldChange={handleFieldChange} />
                  <FormField label="Email Contraparte" name="email_contraparte" type="email" form={form} onFieldChange={handleFieldChange} />
                </div>
                <FormField label="Mandatário da Contraparte" name="mandatario_contraparte" form={form} onFieldChange={handleFieldChange} />
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Contato Mandatário" name="contato_mandatario" form={form} onFieldChange={handleFieldChange} />
                  <FormField label="Email Mandatário" name="email_mandatario" type="email" form={form} onFieldChange={handleFieldChange} />
                </div>
              </>
            )}
            <div className="flex justify-end gap-3 pt-2 border-t border-slate-200 mt-4">
              <button type="button" onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button type="submit" disabled={saving} data-testid="save-processo-btn"
                className="px-4 py-2 bg-[#0b679e] hover:bg-[#084e78] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center gap-2">
                {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>}
                Guardar
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
